export enum RemoteConfigIssue {
  None,
  Warning,
  Error,
}

export class RemoteConfigModel {
  public loadedConfig?: boolean = undefined
  public loadingConfig?: boolean = undefined
  public configError: RemoteConfigIssue = RemoteConfigIssue.None
  public enable_analytics_logging = false
  public show_cpg_tile = true // for hiding during demos
  public netSuiteOrgs: string[] = []
  public bmoIntegrationsWhitelist: string[] = []
  public pushToWalletWhitelist: string[] = []
  public expenseReviewWhitelist: string[] = []
  public enable_sso_login = false // Allows for SSO login and new login flow to be used
  public enable_sso_login_whitelist: string[] = [] // org IDs that have SSO login enabled
  public enable_mixpanel_analytics = true
  public show_bill_pay_history = false
  public show_new_budgets_table = false
  public show_bill_pay_share_later = false
  public show_new_filters_flyout = true
  public show_pnc_cobrand = false
  public bulk_select_fast_follows = false
  public show_brand_redirect = false
  public extendAIWhitelist: string[] = []
  public show_expense_policy_ai: boolean = false
  public show_expense_policy_ai_chat: boolean = false
  public show_expense_policy_ai_whitelist: string[] = []

  public show_ai_insights: boolean = false // enable AI Insights page
  public ai_insights_allowlist: string[] = [] // org IDs that have AI Insights enabled
  public show_extend_ai_agent: boolean = false // enable Extend AI Agent
  public show_suggestions_and_predictions: boolean = false // enable Suggestions and Predictions feature
  public show_sms_settings: boolean = false
  public show_saved_views: boolean = false
  public show_address_suggestions: boolean = false
  public enable_declining_balance_bill_pay: boolean = false // gate showing balance-locked (FIS/Sunflower declining-balance) funding sources in the bill-pay funding dropdown
  public show_dynamic_pricing_page: boolean = false // enable API-driven pricing page
  public enable_embedded_chargebee_checkout: boolean = false // enable Chargebee Embedded Checkout (inline iframe) instead of redirecting to hosted page
  public enable_downgrade_card_limit_modal: boolean = false // EX-39383 — gate redesigned Pro→Starter downgrade flow (over/under VC limit modal + redesigned confirm modal)
  public enable_card_feeds: boolean = false // gate card feeds UI
  public enable_ai_expense_categories_setup_toast: boolean = false // EX-39473 — kill switch for the success toast shown when enabling AI expense category Suggestions / Auto-Coding
  public show_erp_disconnect_ai_warning: boolean = false // EX-39491 — gate scary ERP disconnect modal warning about AI feature loss
  public show_reconnected_erp_expense_category_labels: boolean = false // show stale synced expense category labels after ERP reconnect
  public enable_manage_plan_usage_meters: boolean = false // gate usage meter cards on Manage Plan page
  public enable_task_count: boolean = false // kill switch for the always-mounted tasks-count query (Activity + sidenav); default off after the count-query invalidation amplification incident
  public enable_transaction_activities: boolean = false // gate transaction activity history in the transaction detail flyout
  public enable_transaction_conversations: boolean = false // gate transaction comments in the transaction activity flyout
  public enable_transaction_conversations_allowlist: string[] = [] // org IDs allowed to use transaction comments; must match the API rollout allowlist
  public bulk_invite_csv = false // gate Bulk Invite CSV entry CTAs and the modal flow
  public enable_spend_reporting_dashboard = false // gate Reporting spend dashboard UI
  public enable_spend_reporting_dashboard_whitelist: string[] = [] // org IDs with the Reporting spend dashboard enabled
  public show_updated_physical_card_settings: boolean = false // EX-40354 — gate the redesigned physical card Account Settings tiles (Card Status & Lifecycle, Spend Limit)
  public bill_pay_ach = false // gate the new Bill Pay (ACH / wire / check bank-account rails). Distinct from the LEGACY virtual-card bill pay (show_bill_pay_*, VirtualCardTypes.billPay) that this replaces
  public bill_pay_ach_whitelist: string[] = [] // org IDs that have the new Bill Pay enabled even when bill_pay_ach is globally off
  public bill_pay_vendors_ach = false // gate Bill Pay Vendors (M2) independently of bill_pay_ach (M1 Bank Accounts) — the two milestones must be releasable separately; default off, no pilot org sees the mock-backed page just because it's on the M1 allowlist
  public bill_pay_vendors_ach_whitelist: string[] = [] // org IDs that have Bill Pay Vendors enabled even when bill_pay_vendors_ach is globally off

  public show_updated_card_summary: boolean = false // EX-40350/40351/40352 — single flattened gate for the card summary rework (top bar, card display, action menu, pinned hero) across both summary PRs; itself one of a broader set of flags governing the updated card summary page & tabs. Demo data only.

  public chargebee_base_url = ''
  public chargebee_plan_id = ''
  public chargebee_coupon = ''
  public chargebee_start_timestamp = ''

  public static fromResponse(
    response: RemoteConfigResponse,
  ): RemoteConfigModel {
    const model = this.defaultConfig()
    Object.assign(model, response)
    return model
  }

  public static defaultConfig(): RemoteConfigModel {
    return { ...new RemoteConfigModel() }
  }
}

// any keys added above that should not show in the remote config UI should be added here
// TODO: string[] keys are already filtered out in `FeatureFlagger.tsx` since we don't have a way to modify them in the UI
enum RemoteConfigExclusions {
  loadedConfig,
  loadingConfig,
  configError,
}

export type RemoteConfigResponse = Omit<
  RemoteConfigModel,
  keyof RemoteConfigExclusions
>

export type RemoteConfigKeys = keyof RemoteConfigResponse
export const remoteConfigKeys: RemoteConfigKeys[] = Object.keys(
  RemoteConfigModel.defaultConfig(),
).filter((key) => !(key in RemoteConfigExclusions)) as RemoteConfigKeys[]
